import React from "react";
import { formatPatientInitials } from "../formatUtils";
import { OnboardingVisitInstanceId } from "../messages/ObjectIds";
import { EngagementStatus } from "../messages/Visit";
import { STRINGS } from "../strings";
import { AgencyMember } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { Visit } from "../system/AppState/AppVisitsState";
import { translate } from "../translate";
import { getLocationDisplayLocation } from "../utils/addressComponentUtils";
import { HashSet } from "../utils/HashSet";
import { AgencyDetailsCard } from "./AgencyDetailsCard";
import AvailableVisitDatesAppCard from "./AvailableVisitDatesAppCard";
import AvailableVisitDetails from "./AvailableVisitDetails";
import AvailableVisitFooter from "./AvailableVisitFooter";
import AvailableVisitGeneralNotes from "./AvailableVisitGeneralNotes";
import BadgesGroup from "./BadgesGroup";
import { If } from "./Generic/If";
import ListCard from "./ListCard";
import { MapWidget } from "./MapWidget";
import { CheckedOnboardingVisitData } from "./OnboardingVisitActivity";
import ScaffoldTitle from "./Scaffold/components/ScaffoldTitle";
import Scaffold from "./Scaffold/Scaffold";
import VisitChatIcon from "./VisitChatIcon";
import VisitStaffingPreferencesBadges from "./VisitStaffingPreferencesBadges";

interface Props {
  visit: Visit.Onboarding;
  caregiver: AppCaregiver;
  coordinator: AgencyMember;
  engagementStatus: EngagementStatus;
  countNewPatientChatMessages: number;
  isPopperOpen: boolean;
  checkedVisits: HashSet<OnboardingVisitInstanceId>;
  checkedPatterns: HashSet<string>;
  areAllShiftsChecked: boolean;
  onClickLocation: () => void;
  onClickGoBack: () => void;
  onClickChat: () => void;
  onClickRequest: () => void;
  onClickReject: () => void;
  onClickCancel: () => void;
  onCheckVisit: (visitData: CheckedOnboardingVisitData) => void;
  onClickCall: () => void;
}

const AvailableVisitScreenOnboarding = (props: Props) => {
  const chatAction = (
    <VisitChatIcon
      badge={props.countNewPatientChatMessages}
      isPopperOpen={props.isPopperOpen}
      onClick={props.onClickChat}
    />
  );

  const isRequestPermitted = (() => {
    return (
      props.checkedVisits.size() > 0 ||
      props.checkedPatterns.size() > 0 ||
      props.visit.publicVisitBroadcastDetails.visitInstances.length === 1
    );
  })();

  const isSingleShift = (() => {
    return (
      props.visit.publicVisitBroadcastDetails.visitInstances.length === 1 ||
      props.checkedVisits.size() === 1
    );
  })();

  return (
    <Scaffold
      title={
        <ScaffoldTitle
          label={getLocationDisplayLocation(props.visit.publicVisitBroadcastDetails.location)}
        />
      }
      footer={
        <AvailableVisitFooter
          status={props.engagementStatus}
          onClickRequest={props.onClickRequest}
          onClickReject={props.onClickReject}
          onClickCancel={props.onClickCancel}
          isRequestPermitted={isRequestPermitted}
          isSingleShift={isSingleShift}
        />
      }
      endActions={chatAction}
      onClickGoBack={props.onClickGoBack}
      className="AvailableVisitScreen"
      overlayHeader={true}
    >
      <MapWidget
        origin={props.caregiver.addressGeoLocation}
        destination={props.visit.publicVisitBroadcastDetails.location.location}
        patientInitials={formatPatientInitials(
          props.visit.publicVisitBroadcastDetails.patientInitials
        )}
        onOpenMapClick={props.onClickLocation}
      />

      <BadgesGroup labels={props.visit.publicVisitBroadcastDetails.visitLabels} />

      <If condition={props.visit.publicVisitBroadcastDetails.agency.isMedflytAgency}>
        <VisitStaffingPreferencesBadges
          staffingPreferences={props.visit.publicVisitBroadcastDetails.staffingPreferences}
        />
      </If>

      <AvailableVisitDatesAppCard
        broadcastDetails={props.visit.publicVisitBroadcastDetails}
        checkedVisits={props.checkedVisits}
        checkedPatterns={props.checkedPatterns}
        requestedVisitBatch={props.visit.caregiverVisitEngagementDetails.requested}
        areAllShiftsChecked={props.areAllShiftsChecked}
        visitType={props.visit.type}
        flexibleVisitProps={null}
        onCheckVisit={props.onCheckVisit}
      />

      <AvailableVisitDetails
        patientName={props.visit.publicVisitBroadcastDetails.patientFirstName}
        patientAge={props.visit.publicVisitBroadcastDetails.patientAge}
        patientGender={props.visit.publicVisitBroadcastDetails.patientGender}
        patientLanguages={props.visit.publicVisitBroadcastDetails.patientLanguages}
        broadcastDetails={props.visit.publicVisitBroadcastDetails}
        caregiverLocation={props.caregiver.addressGeoLocation}
        onClickLocation={props.onClickLocation}
        checkedVisits={props.checkedVisits}
        onCheckVisit={props.onCheckVisit}
        checkedPatterns={props.checkedPatterns}
        requestedVisitBatch={props.visit.caregiverVisitEngagementDetails.requested}
        areAllShiftsChecked={props.areAllShiftsChecked}
        visitType={props.visit.type}
        isMedflytCaregiver={props.caregiver.isCaregiverOfMedFlyt}
      />

      <If condition={props.visit.publicVisitBroadcastDetails.agency.isMedflytAgency}>
        <ListCard
          title={translate(STRINGS.PATIENT_REQUESTS)}
          items={props.visit.publicVisitBroadcastDetails.patientRequests}
        />

        <ListCard
          title={translate(STRINGS.MAIN_TASKS)}
          items={props.visit.publicVisitBroadcastDetails.patientTasks}
        />
      </If>

      <AvailableVisitGeneralNotes notes={props.visit.publicVisitBroadcastDetails.generalNotes} />

      <AgencyDetailsCard
        agency={props.visit.publicVisitBroadcastDetails.agency}
        coordinatorInCharge={props.coordinator}
        onClickChat={props.onClickChat}
        onClickCall={props.onClickCall}
        showCallButton={!props.visit.publicVisitBroadcastDetails.agency.isMedflytAgency}
      />
    </Scaffold>
  );
};

export default AvailableVisitScreenOnboarding;
